import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

const DAYS_FULL = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const SESSIONS = [
  { key: "session_1", label: "8:00–10:00" },
  { key: "session_2", label: "10:30–12:30" },
  { key: "session_3", label: "1:30–3:30" },
];

interface DayEntry {
  day: string;
  sessions: string[];
}

interface ModuleAvailabilityRendererProps {
  selectedModules: string[];
  answer: Record<string, DayEntry[]> | undefined;
  onAnswer: (value: Record<string, DayEntry[]>) => void;
  lang?: "en" | "sw";
}

const ModuleAvailabilityRenderer = ({
  selectedModules,
  answer,
  onAnswer,
  lang = "en",
}: ModuleAvailabilityRendererProps) => {
  const data: Record<string, DayEntry[]> = answer || {};

  const getEntries = (mod: string): DayEntry[] => data[mod] || [];

  const addDay = (mod: string) => {
    const entries = getEntries(mod);
    // Find first day not already used
    const usedDays = entries.map(e => e.day);
    const nextDay = DAYS_FULL.find(d => !usedDays.includes(d)) || DAYS_FULL[0];
    onAnswer({ ...data, [mod]: [...entries, { day: nextDay, sessions: [] }] });
  };

  const removeDay = (mod: string, idx: number) => {
    const entries = [...getEntries(mod)];
    entries.splice(idx, 1);
    if (entries.length === 0) {
      const updated = { ...data };
      delete updated[mod];
      onAnswer(updated);
    } else {
      onAnswer({ ...data, [mod]: entries });
    }
  };

  const changeDay = (mod: string, idx: number, newDay: string) => {
    const entries = [...getEntries(mod)];
    entries[idx] = { ...entries[idx], day: newDay };
    onAnswer({ ...data, [mod]: entries });
  };

  const toggleSession = (mod: string, idx: number, sessionKey: string) => {
    const entries = [...getEntries(mod)];
    const entry = entries[idx];
    const sessions = entry.sessions.includes(sessionKey)
      ? entry.sessions.filter(s => s !== sessionKey)
      : [...entry.sessions, sessionKey];
    entries[idx] = { ...entry, sessions };
    onAnswer({ ...data, [mod]: entries });
  };

  const isWeekend = (day: string) => day === "Saturday" || day === "Sunday";

  const getVisibleSessions = (day: string) =>
    isWeekend(day) ? SESSIONS.filter(s => s.key !== "session_3") : SESSIONS;

  if (selectedModules.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">
        {lang === "sw" ? "Chagua moduli kwanza" : "Select modules first to set availability"}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        {lang === "sw"
          ? "Chagua siku na vipindi unavyopatikana kufundisha kila moduli. Unaweza kuongeza siku nyingi."
          : "Select the days and sessions you're available to teach each module. You can add multiple days."}
      </p>

      {selectedModules.map((mod) => {
        const entries = getEntries(mod);
        return (
          <div key={mod} className="border rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-foreground line-clamp-1">{mod}</h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={() => addDay(mod)}
                disabled={entries.length >= DAYS_FULL.length}
              >
                <Plus className="h-3 w-3" />
                {lang === "sw" ? "Ongeza Siku" : "Add Day"}
              </Button>
            </div>

            {entries.length === 0 && (
              <p className="text-xs text-muted-foreground italic">
                {lang === "sw" ? "Bonyeza 'Ongeza Siku' kuchagua upatikanaji" : "Click 'Add Day' to set your availability"}
              </p>
            )}

            {entries.map((entry, idx) => {
              const usedDays = entries.map((e, i) => i !== idx ? e.day : null).filter(Boolean) as string[];
              const availableDays = DAYS_FULL.filter(d => !usedDays.includes(d));
              const visibleSessions = getVisibleSessions(entry.day);

              return (
                <div key={idx} className="flex flex-col sm:flex-row items-start sm:items-center gap-2 bg-muted/30 rounded-md p-2">
                  <div className="flex items-center gap-2 min-w-[140px]">
                    <Select value={entry.day} onValueChange={(v) => changeDay(mod, idx, v)}>
                      <SelectTrigger className="h-8 text-xs w-[130px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availableDays.map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                        {/* Always include current day */}
                        {!availableDays.includes(entry.day) && (
                          <SelectItem value={entry.day}>{entry.day}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => removeDay(mod, idx)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  <div className="flex items-center gap-3 flex-wrap">
                    {visibleSessions.map(s => (
                      <label key={s.key} className="flex items-center gap-1.5 cursor-pointer">
                        <Checkbox
                          checked={entry.sessions.includes(s.key)}
                          onCheckedChange={() => toggleSession(mod, idx, s.key)}
                        />
                        <span className="text-xs text-foreground whitespace-nowrap">{s.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      <p className="text-[0.65rem] text-muted-foreground">
        {lang === "sw"
          ? "Kipindi cha 3 (1:30–3:30) hakipatikani Jumamosi/Jumapili"
          : "Session 3 (1:30–3:30) is not available on weekends"}
      </p>
    </div>
  );
};

export default ModuleAvailabilityRenderer;
